(() => {
  "use strict";

  // ------------------------------------------------------------
  // Config / State
  // ------------------------------------------------------------
  const STATE = {
    busyATC: false,
    stableTimer: null,
    stableSig: "",
    stableHits: 0,
    lastScheduleAt: 0,
    observerStarted: false,
    layoutRaf: 0,
  };

  const DETAILS_FALLBACK = "https://gasland24.de/pages/garantiebedingungen";

  // Some merchants prefer the cart bubble to count only main products (exclude warranty lines).
  // Horizon uses cart.item_count (includes all lines). We normalize via /cart.js and update the bubble.
  const BUBBLE = {
    lastFetchAt: 0,
    timer: null,
    storageKey: "warranty:main-cart-count",
  };

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------
  const $ = (sel, root = document) => root.querySelector(sel);

  function safeNum(v, fallback = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function getAllCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((item) => item.getAttribute('data-section-id'))
      .filter(Boolean);
  }

  async function fetchCartJson() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error('cart.js failed');
    return res.json();
  }

  async function fetchSectionsMap(sectionIds) {
    const ids = Array.from(new Set((sectionIds || []).filter(Boolean)));
    if (!ids.length) return {};
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    const res = await fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error(`sections fetch failed (${res.status})`);
    const data = await res.json().catch(() => ({}));
    return data && typeof data === 'object' ? data : {};
  }

  function findClosestForm(el) {
    if (!el) return null;
    const form = el.closest('form[action="/cart/add"]');
    return form || null;
  }

  function openDrawerIfPossible() {
    const open = () => {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    };
    open();
    window.setTimeout(open, 60);
    window.setTimeout(open, 220);
  }

  function persistCartBubbleCount(count) {
    try {
      sessionStorage.setItem(BUBBLE.storageKey, String(Math.max(0, Number(count || 0))));
    } catch (_) {
      // ignore
    }
  }

  function readPersistedCartBubbleCount() {
    try {
      const raw = sessionStorage.getItem(BUBBLE.storageKey);
      if (raw == null) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
    } catch (_) {
      return null;
    }
  }

  function getCurrentMainCartBubbleCount() {
    const persisted = readPersistedCartBubbleCount();
    if (persisted != null) return persisted;

    const el = document.querySelector('.cart-bubble__text-count, .cart-bubble__count, [data-cart-bubble-count]');
    const parsed = Number.parseInt((el?.textContent || '').replace(/\D+/g, ''), 10);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  }

  function getMainProductQuantityFromItems(items) {
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, item) => {
      const props = item?.properties || {};
      const isWarranty = String(props._is_warranty || '') === '1';
      return sum + (isWarranty ? 0 : Number(item?.quantity || 0));
    }, 0);
  }


  function getMainQtyForVariant(cart, variantId) {
    const vid = String(variantId || '').trim();
    if (!vid || !Array.isArray(cart?.items)) return 0;
    return cart.items.reduce((sum, item) => {
      const props = item?.properties || {};
      if (String(props._is_warranty || '') === '1') return sum;
      if (String(props._is_accessory_bundle || '') === '1') return sum;
      if (String(item?.variant_id || '').trim() !== vid) return sum;
      return sum + Number(item?.quantity || 0);
    }, 0);
  }

  function findMainLineForVariant(cart, variantId) {
    const vid = String(variantId || '').trim();
    if (!vid || !Array.isArray(cart?.items)) return null;

    return cart.items.find((item) => {
      const props = item?.properties || {};
      if (String(props._is_warranty || '') === '1') return false;
      if (String(props._is_accessory_bundle || '') === '1') return false;
      return String(item?.variant_id || '').trim() === vid;
    }) || null;
  }

  function resolveProductErrorElement(form) {
    return form?.querySelector?.('[ref="addToCartTextError"], [data-add-to-cart-error]')
      || form?.closest?.('product-form-component')?.querySelector?.('[ref="addToCartTextError"], [data-add-to-cart-error]')
      || null;
  }

  function setProductFormInlineMessage(form, message) {
    const el = resolveProductErrorElement(form);
    if (!(el instanceof HTMLElement)) return;
    if (!message) {
      el.classList.add('hidden');
      return;
    }
    el.classList.remove('hidden');
    const textNode = el.childNodes[2];
    if (textNode) textNode.textContent = message;
    else el.appendChild(document.createTextNode(message));
  }

  function syncProductFormQuantity(form, nextQuantity) {
    const safeQuantity = Math.max(1, Number(nextQuantity || 1));
    if (!(form instanceof HTMLFormElement)) return;
    form.querySelectorAll('[name="quantity"]').forEach((input) => {
      try { input.value = String(safeQuantity); } catch (_) {}
      try { input.defaultValue = String(safeQuantity); } catch (_) {}
      try { input.setAttribute('value', String(safeQuantity)); } catch (_) {}
      try { input.dataset.cartQuantity = String(safeQuantity); } catch (_) {}
      try { input.dispatchEvent(new Event('input', { bubbles: true })); } catch (_) {}
      try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) {}
    });
    const host = form.closest('product-form-component');
    const quantitySelector = host?.refs?.quantitySelector || form.querySelector('quantity-selector-component, quantity-selector') || host?.querySelector?.('quantity-selector-component, quantity-selector');
    if (quantitySelector && typeof quantitySelector.setValue === 'function') {
      try { quantitySelector.setValue(String(safeQuantity)); } catch (_) {}
    }
  }

  function dispatchCartUpdate(resource, itemCount) {
    const detail = {
      resource,
      data: {
        sections: resource?.sections || {},
      },
    };

    if (Number.isFinite(Number(itemCount))) {
      detail.itemCount = Number(itemCount);
      detail.data.itemCount = Number(itemCount);
    }

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail,
    }));
  }

  function optimisticallyIncreaseMainCartBubble(items) {
    const nextCount = getCurrentMainCartBubbleCount() + getMainProductQuantityFromItems(items);
    setCartBubbleCount(nextCount);
  }

  function setCartBubbleCount(count) {
    const safeCount = Math.max(0, Number(count || 0));
    const els = document.querySelectorAll(
      ".cart-bubble__text-count, .cart-bubble__count, [data-cart-bubble-count]"
    );

    els.forEach((el) => {
      try {
        el.textContent = safeCount > 99 ? "99+" : String(safeCount);
        el.classList.toggle("hidden", safeCount === 0);
      } catch (_) {}

      const bubble = el.closest('.cart-bubble');
      if (bubble) {
        bubble.classList.toggle('visually-hidden', safeCount === 0);
      }
    });

    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', safeCount === 0);
    });

    persistCartBubbleCount(safeCount);
    window.__warrantyMainCartCount = safeCount;
  }

  async function normalizeCartBubbleCount(force = false) {
    const now = Date.now();
    if (!force && now - BUBBLE.lastFetchAt < 800) return;
    BUBBLE.lastFetchAt = now;

    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      if (!res.ok) return;
      const cart = await res.json();
      if (!cart || !Array.isArray(cart.items)) return;

      const count = cart.items.reduce((sum, it) => {
        const isWarranty = it?.properties && String(it.properties._is_warranty || "") === "1";
        return sum + (isWarranty ? 0 : Number(it.quantity || 0));
      }, 0);
      setCartBubbleCount(count);
    } catch (_) {
      // ignore
    }
  }

  // ------------------------------------------------------------
  // Sections replacement (cart icon bubble in header)
  // ------------------------------------------------------------
  function getDrawerCartItemsSectionId() {
    // Horizon: drawer cart items component often carries section id
    const el = document.querySelector('cart-items-component[data-drawer][data-section-id]');
    return el ? el.getAttribute("data-section-id") : null;
  }

  function replaceSectionHTML(sectionId, html) {
    if (!sectionId || !html) return false;

    const cur = document.querySelector(`#shopify-section-${CSS.escape(sectionId)}`);
    if (!cur) return false;

    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const next = doc.querySelector(`#shopify-section-${CSS.escape(sectionId)}`);
    if (next) {
      cur.replaceWith(next);
      return true;
    }

    cur.innerHTML = doc.body.innerHTML;
    return true;
  }

  function applySectionsMap(sections) {
    if (!sections || typeof sections !== 'object') return false;
    let didReplace = false;
    Object.entries(sections).forEach(([sectionId, html]) => {
      didReplace = replaceSectionHTML(sectionId, html) || didReplace;
    });
    return didReplace;
  }

  function refreshHeaderState() {
    window.cancelAnimationFrame(STATE.layoutRaf);
    STATE.layoutRaf = window.requestAnimationFrame(() => {
      STATE.layoutRaf = window.requestAnimationFrame(() => {
        const header = document.querySelector("header-component");
        const headerGroup = document.querySelector("#header-group");
        const hasHeaderSection = headerGroup?.querySelector(".header-section");

        if (header && headerGroup) {
          const headerTopRow = header.querySelector(".header__row--top");
          const headerHeight = header.offsetHeight;

          let headerGroupHeight = 0;
          Array.from(headerGroup.children).forEach((element) => {
            if (!(element instanceof HTMLElement) || element === header) return;
            headerGroupHeight += element.offsetHeight;
          });

          if (header.hasAttribute("transparent") && header.parentElement?.nextElementSibling) {
            headerGroupHeight += headerHeight;
          }

          document.body.style.setProperty("--header-height", `${headerHeight}px`);
          document.body.style.setProperty("--header-group-height", `${headerGroupHeight}px`);

          if (headerTopRow) {
            document.body.style.setProperty("--top-row-height", `${headerTopRow.offsetHeight}px`);
          }

          if (!hasHeaderSection || !header.hasAttribute("transparent")) {
            document.body.style.setProperty("--transparent-header-offset-boolean", "0");
          } else {
            const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains("shopify-section");
            document.body.style.setProperty(
              "--transparent-header-offset-boolean",
              hasImmediateSection ? "0" : "1"
            );
          }
        }

        const headerComponent = document.querySelector("#header-component");
        if (headerComponent) {
          const isTouchDevice = "ontouchstart" in window && navigator.maxTouchPoints > 0;
          const overflowList = headerComponent.querySelector("overflow-list");
          const hasReachedMinimum = overflowList?.hasAttribute("minimum-reached");
          headerComponent.dataset.menuStyle = isTouchDevice || hasReachedMinimum ? "drawer" : "menu";
        }

        window.dispatchEvent(new Event("resize"));
      });
    });
  }

  async function cartAddWithSections(items) {
    const sectionIds = getAllCartSectionIds();
    const payload = {
      items,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname + window.location.search,
    };

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`cart/add.js failed (${res.status}): ${t}`);
    }

    const data = await res.json();
    let sections = data.sections && typeof data.sections === 'object' ? { ...data.sections } : {};

    if (sectionIds.length) {
      let hasAll = true;
      sectionIds.forEach((id) => { if (!sections[id]) hasAll = false; });
      if (!hasAll) {
        const fetched = await fetchSectionsMap(sectionIds).catch(() => ({}));
        sections = { ...sections, ...fetched };
      }
    }

    const resource = { ...data, sections };
    const didReplaceSection = applySectionsMap(sections);
    if (didReplaceSection) {
      refreshHeaderState();
      dispatchCartUpdate(resource);
    }

    scheduleDrawerStablePass();
    window.setTimeout(scheduleDrawerStablePass, 220);
    normalizeCartBubbleCount(true);
    return resource;
  }

  async function cartChangeWithSections(id, quantity) {
    const sectionIds = getAllCartSectionIds();
    const payload = {
      id,
      quantity,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname + window.location.search,
    };

    const res = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`cart/change.js failed (${res.status}): ${t}`);
    }

    const data = await res.json();
    let sections = data.sections && typeof data.sections === 'object' ? { ...data.sections } : {};
    if (sectionIds.length) {
      let hasAll = true;
      sectionIds.forEach((id) => { if (!sections[id]) hasAll = false; });
      if (!hasAll) {
        const fetched = await fetchSectionsMap(sectionIds).catch(() => ({}));
        sections = { ...sections, ...fetched };
      }
    }

    const resource = { ...data, sections };
    if (applySectionsMap(sections)) {
      refreshHeaderState();
      dispatchCartUpdate(resource, getMainProductQuantityFromItems(data.items));
    }

    scheduleDrawerStablePass();
    normalizeCartBubbleCount(true);
    return resource;
  }


  // ------------------------------------------------------------
  // Product page ATC: add parent + optional warranty IN ONE request
  // Prevents "2x product" caused by theme add + warranty add double.
  // ------------------------------------------------------------
  function getProductWarrantyBox() {
    return document.querySelector('[data-warranty-box][data-scope="product"]');
  }

  function getAccessoriesBlockForForm(form) {
    if (!form) return null;

    const scopedRoot = form.closest('.shopify-section, dialog, product-card') || document;
    const scopedBlocks = Array.from(scopedRoot.querySelectorAll('accessories-bundle-block'));
    if (!scopedBlocks.length) return document.querySelector('accessories-bundle-block');
    if (scopedBlocks.length === 1) return scopedBlocks[0];

    const productId = String(
      form.closest('product-form-component')?.getAttribute('data-product-id') ||
      form.getAttribute('data-product-id') ||
      ''
    );

    if (productId) {
      const matched = scopedBlocks.find((block) => String(block.getAttribute('data-product-id') || '') === productId);
      if (matched) return matched;
    }

    return scopedBlocks[0] || null;
  }

  function getSelectedAccessoryItemsForForm(form, parentVariantId) {
    const block = getAccessoriesBlockForForm(form);
    if (!block) return [];

    const parentProductId = String(
      block.getAttribute('data-product-id') ||
      form.closest('product-form-component')?.getAttribute('data-product-id') ||
      ''
    );
    const parentTitle = String(block.getAttribute('data-product-title') || '');
    const uniqueItems = new Map();

    block.querySelectorAll('[data-variant-id]').forEach((card) => {
      const checkbox = card.querySelector('.ai-complementary-products__checkbox');
      if (!checkbox || checkbox.disabled || !checkbox.checked) return;

      const variantId = String(card.getAttribute('data-variant-id') || '').trim();
      if (!variantId || variantId === parentVariantId || uniqueItems.has(variantId)) return;

      const id = safeNum(variantId, 0);
      if (id <= 0) return;

      uniqueItems.set(variantId, {
        id,
        quantity: 1,
        properties: {
          _is_accessory_bundle: '1',
          _bundle_parent_product_id: parentProductId,
          _bundle_parent_variant_id: parentVariantId,
          _bundle_parent_title: parentTitle,
        },
      });
    });

    return Array.from(uniqueItems.values());
  }

  function getProductFormForWarrantyBox(box) {
    if (!box) return null;
    const scopedRoot = box.closest('.shopify-section, dialog, product-card') || document;
    return scopedRoot.querySelector('form[action="/cart/add"]') || document.querySelector('form[action="/cart/add"]');
  }

  function getCurrentParentVariantIdForBox(box) {
    const form = getProductFormForWarrantyBox(box);
    if (!(form instanceof HTMLFormElement)) return String(box?.getAttribute('data-parent-variant-id') || '').trim();
    const value = new FormData(form).get('id');
    return value ? String(value).trim() : String(box?.getAttribute('data-parent-variant-id') || '').trim();
  }

  function findWarrantyLineForParent(cart, parentVariantId) {
    const vid = String(parentVariantId || '').trim();
    if (!vid || !Array.isArray(cart?.items)) return null;

    return cart.items.find((item) => {
      const props = item?.properties || {};
      return String(props._is_warranty || '') === '1' && String(props._parent_variant_id || '').trim() === vid;
    }) || null;
  }

  function setProductWarrantyBoxVisual(box, opts = {}) {
    if (!box) return;
    const checked = !!opts.checked;
    const state = String(opts.state || 'pending');
    const lineKey = String(opts.lineKey || '').trim();
    const toggle = box.querySelector('[data-warranty-toggle]');
    const status = box.querySelector('[data-warranty-status-trigger]');
    const icon = status?.querySelector('.warranty-box__status-icon');
    const textNode = status?.querySelector('.warranty-box__status-text');

    if (toggle) toggle.checked = checked;
    box.setAttribute('data-warranty-line-key', lineKey);

    if (status) {
      status.classList.remove('is-added', 'is-selected', 'is-pending');

      if (state === 'added') {
        status.classList.add('is-added');
        if (icon) icon.textContent = '✓';
        if (textNode) textNode.textContent = 'hinzugefügt';
      } else if (state === 'selected') {
        status.classList.add('is-selected');
        if (icon) icon.textContent = '✓';
        if (textNode) textNode.textContent = 'Ausgewählt';
      } else {
        status.classList.add('is-pending');
        if (icon) icon.textContent = '☞';
        if (textNode) textNode.textContent = 'Service hinzufügen';
      }
    }
  }

  async function syncProductWarrantyBoxFromCart() {
    const box = getProductWarrantyBox();
    if (!box) return;

    const parentVariantId = getCurrentParentVariantIdForBox(box);
    box.setAttribute('data-parent-variant-id', parentVariantId);

    if (!parentVariantId) {
      setProductWarrantyBoxVisual(box, { checked: false, state: 'pending', lineKey: '' });
      return;
    }

    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) return;
      const cart = await res.json();
      const line = findWarrantyLineForParent(cart, parentVariantId);
      if (line) {
        setProductWarrantyBoxVisual(box, { checked: true, state: 'added', lineKey: line.key || '' });
      } else {
        const toggle = box.querySelector('[data-warranty-toggle]');
        const wantsWarranty = !!(toggle && toggle.checked);
        setProductWarrantyBoxVisual(box, { checked: wantsWarranty, state: wantsWarranty ? 'selected' : 'pending', lineKey: '' });
      }
    } catch (_) {
      const toggle = box.querySelector('[data-warranty-toggle]');
      const wantsWarranty = !!(toggle && toggle.checked);
      setProductWarrantyBoxVisual(box, { checked: wantsWarranty, state: wantsWarranty ? 'selected' : 'pending', lineKey: '' });
    }
  }

  async function addProductWarrantyForExistingParent(box) {
    if (!box) return null;

    const parentVariantId = getCurrentParentVariantIdForBox(box);
    const warrantyVariantId = String(box.getAttribute('data-warranty-variant-id') || '').trim();
    if (!parentVariantId || !warrantyVariantId) return null;

    const cart = await fetchCartJson();
    const parentLine = findMainLineForVariant(cart, parentVariantId);
    const parentQty = getMainQtyForVariant(cart, parentVariantId);

    if (!parentLine || parentQty <= 0) return null;

    const item = {
      id: safeNum(warrantyVariantId, 0),
      quantity: parentQty,
      properties: {
        _is_warranty: '1',
        _warranty_label: String(box.getAttribute('data-warranty-label') || '36 Monate Langzeitgarantie'),
        _warranty_details_url: String(box.getAttribute('data-warranty-details-url') || DETAILS_FALLBACK),
        _parent_variant_id: parentVariantId,
        _parent_line_key: String(parentLine.key || ''),
        _parent_sku: String(box.getAttribute('data-parent-sku') || ''),
        _parent_mpn: String(box.getAttribute('data-parent-mpn') || ''),
      },
    };

    await cartAddWithSections([item]);
    return fetchCartJson();
  }

  async function handleProductWarrantyToggleChange(toggle) {
    const box = toggle?.closest?.('[data-warranty-box][data-scope="product"]');
    if (!box) return;

    const lineKey = String(box.getAttribute('data-warranty-line-key') || '').trim();

    if (toggle.checked) {
      if (lineKey) {
        setProductWarrantyBoxVisual(box, { checked: true, state: 'added', lineKey });
        return;
      }

      try {
        toggle.disabled = true;
        const cart = await addProductWarrantyForExistingParent(box);
        const warrantyLine = cart ? findWarrantyLineForParent(cart, getCurrentParentVariantIdForBox(box)) : null;

        if (warrantyLine) {
          setProductWarrantyBoxVisual(box, { checked: true, state: 'added', lineKey: warrantyLine.key || '' });
          openDrawerIfPossible();
          return;
        }
      } catch (err) {
        console.error('[warranty] immediate product warranty add failed:', err);
      } finally {
        try { toggle.disabled = false; } catch (_) {}
      }

      setProductWarrantyBoxVisual(box, { checked: true, state: 'selected', lineKey: '' });
      return;
    }

    if (!lineKey) {
      setProductWarrantyBoxVisual(box, { checked: false, state: 'pending', lineKey: '' });
      return;
    }

    try {
      toggle.disabled = true;
      await cartChangeWithSections(lineKey, 0);
      setProductWarrantyBoxVisual(box, { checked: false, state: 'pending', lineKey: '' });
      openDrawerIfPossible();
      syncProductWarrantyBoxFromCart();
    } catch (err) {
      console.error('[warranty] remove error:', err);
      setProductWarrantyBoxVisual(box, { checked: true, state: 'added', lineKey });
    } finally {
      try { toggle.disabled = false; } catch (_) {}
    }
  }

  function onProductWarrantyStatusTriggerClick(event) {
    const trigger = event.target.closest('[data-warranty-box][data-scope="product"] [data-warranty-status-trigger]');
    if (!trigger) return false;

    const box = trigger.closest('[data-warranty-box][data-scope="product"]');
    const toggle = box?.querySelector('[data-warranty-toggle]');
    if (!toggle) return true;

    event.preventDefault();
    toggle.checked = !toggle.checked;
    toggle.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function buildATCItemsFromProductForm(form, box) {
    const fd = new FormData(form);

    const parentVariantId = String(fd.get("id") || "").trim();
    const parentQty = Math.max(1, safeNum(fd.get("quantity"), 1));

    if (!parentVariantId) return [];

    const toggle = box ? box.querySelector("[data-warranty-toggle]") : null;
    const wantWarranty = !!(toggle && toggle.checked);

    const warrantyVariantId = box ? String(box.getAttribute("data-warranty-variant-id") || "").trim() : "";
    const label = box ? String(box.getAttribute("data-warranty-label") || "36 Monate Langzeitgarantie") : "36 Monate Langzeitgarantie";
    const detailsUrl = box ? String(box.getAttribute("data-warranty-details-url") || DETAILS_FALLBACK) : DETAILS_FALLBACK;

    // parent sku/mpn for warranty properties
    const parentSku = box ? String(box.getAttribute("data-parent-sku") || "") : "";
    const parentMpn = box ? String(box.getAttribute("data-parent-mpn") || "") : "";

    const items = [{ id: safeNum(parentVariantId, 0), quantity: parentQty }];

    const accessoryItems = getSelectedAccessoryItemsForForm(form, parentVariantId);
    if (accessoryItems.length) items.push(...accessoryItems);

    if (wantWarranty && warrantyVariantId) {
      items.push({
        id: safeNum(warrantyVariantId, 0),
        quantity: parentQty,
        properties: {
          _is_warranty: "1",
          _warranty_label: label,
          _warranty_details_url: detailsUrl,
          _parent_variant_id: parentVariantId,
          _parent_sku: parentSku,
          _parent_mpn: parentMpn,
        },
      });
    }

    return items.filter((i) => i.id > 0);
  }


  async function finalizeProductFormAdd(form, box, parentVariantId, requestedQty, wantWarranty, beforeCart) {
    try {
      let cart = await fetchCartJson();
      const beforeMainQty = getMainQtyForVariant(beforeCart, parentVariantId);
      const afterMainQty = getMainQtyForVariant(cart, parentVariantId);
      const acceptedAddedQty = Math.max(0, afterMainQty - beforeMainQty);

      if (acceptedAddedQty > 0) {
        syncProductFormQuantity(form, acceptedAddedQty);
      }

      if (acceptedAddedQty < requestedQty) {
        setProductFormInlineMessage(form, `Aufgrund der Verfügbarkeit wurden nur ${acceptedAddedQty} Artikel zu deinem Warenkorb hinzugefügt.`);
      } else {
        setProductFormInlineMessage(form, '');
      }

      if (wantWarranty) {
        const warrantyLine = findWarrantyLineForParent(cart, parentVariantId);
        if (warrantyLine && Number(warrantyLine.quantity || 0) !== afterMainQty) {
          await cartChangeWithSections(warrantyLine.key, afterMainQty);
          cart = await fetchCartJson();
        }
      }

      window.__warrantySetCartBubbleCount?.(getMainProductQuantityFromItems(cart.items));
      await syncProductWarrantyBoxFromCart();
      openDrawerIfPossible();
      return cart;
    } catch (error) {
      console.warn('[warranty] finalize product add failed', error);
      return null;
    }
  }

  function isClickOnATCSubmitButton(e) {
    const btn = e.target.closest('button[type="submit"], input[type="submit"]');
    if (!btn) return false;
    const form = findClosestForm(btn);
    return !!form;
  }

  async function handleATCClickCapture(e) {
    const box = getProductWarrantyBox();
    if (!box) return;

    if (!isClickOnATCSubmitButton(e)) return;

    const btn = e.target.closest('button[type="submit"], input[type="submit"]');
    const form = findClosestForm(btn);
    if (!form) return;

    // Take over ATC to avoid double adds
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (STATE.busyATC) return;
    STATE.busyATC = true;

    try {
      if (btn) btn.disabled = true;
      const items = buildATCItemsFromProductForm(form, box);
      if (!items.length) return;
      const requestedQty = Number(items[0]?.quantity || 1);
      const parentVariantId = String(items[0]?.id || '');
      const wantWarranty = items.some((item) => String(item?.properties?._is_warranty || '') === '1');
      const beforeCart = await fetchCartJson().catch(() => null);
      optimisticallyIncreaseMainCartBubble(items);
      await cartAddWithSections(items);
      await finalizeProductFormAdd(form, box, parentVariantId, requestedQty, wantWarranty, beforeCart);
    } catch (err) {
      console.error("[warranty] ATC error:", err);
      normalizeCartBubbleCount(true);
    } finally {
      try { if (btn) btn.disabled = false; } catch (_) {}
      STATE.busyATC = false;
    }
  }

  async function handleATCSubmitCapture(e) {
    // fallback for "enter submit" on form
    const box = getProductWarrantyBox();
    if (!box) return;

    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute("action") !== "/cart/add") return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (STATE.busyATC) return;
    STATE.busyATC = true;

    const btn = form.querySelector('button[type="submit"], input[type="submit"]');

    try {
      if (btn) btn.disabled = true;
      const items = buildATCItemsFromProductForm(form, box);
      if (!items.length) return;
      const requestedQty = Number(items[0]?.quantity || 1);
      const parentVariantId = String(items[0]?.id || '');
      const wantWarranty = items.some((item) => String(item?.properties?._is_warranty || '') === '1');
      const beforeCart = await fetchCartJson().catch(() => null);
      optimisticallyIncreaseMainCartBubble(items);
      await cartAddWithSections(items);
      await finalizeProductFormAdd(form, box, parentVariantId, requestedQty, wantWarranty, beforeCart);
    } catch (err) {
      console.error("[warranty] submit error:", err);
      normalizeCartBubbleCount(true);
    } finally {
      try { if (btn) btn.disabled = false; } catch (_) {}
      STATE.busyATC = false;
    }
  }

  // ------------------------------------------------------------
  // Drawer reorder + divider (robust: shadowRoot + multi-pass)
  // ------------------------------------------------------------
  function getDrawerHosts() {
    const hosts = [];
    const d = document.querySelector("cart-drawer-component");
    if (d) hosts.push(d);

    const ci = document.querySelector("cart-items-component[data-drawer]");
    if (ci && !hosts.includes(ci)) hosts.push(ci);

    return hosts;
  }

  function getSearchRoots(host) {
    const roots = [];
    if (!host) return roots;
    roots.push(host);
    if (host.shadowRoot) roots.push(host.shadowRoot);
    return roots;
  }

  function getDrawerRows() {
    const hosts = getDrawerHosts();
    for (const host of hosts) {
      const roots = getSearchRoots(host);
      for (const r of roots) {
        const rows = Array.from(r.querySelectorAll("[data-cart-line][data-line-key]"));
        if (rows.length) return { rows, root: r };
      }
    }
    return { rows: [], root: null };
  }

  function rowsSignature(rows) {
    return rows
      .map((el) =>
        [
          el.getAttribute("data-line-key") || "",
          el.getAttribute("data-is-warranty") || "",
          el.getAttribute("data-is-accessory") || "",
          el.getAttribute("data-accessory-parent-key") || "",
          el.getAttribute("data-parent-key") || "",
          el.getAttribute("data-warranty-parent-variant-id") || "",
          el.getAttribute("data-variant-id") || "",
        ].join(":")
      )
      .join("|");
  }

  function isWarrantyRow(row) {
    if (!row) return false;
    return row.getAttribute("data-is-warranty") === "1" || row.classList.contains("cart-items__warranty-line");
  }

  function isAccessoryRow(row) {
    if (!row) return false;
    if (row.getAttribute("data-is-accessory") === "1") return true;
    if (row.getAttribute("data-accessory-parent-key")) return true;
    if (row.classList.contains("cart-items__accessory-line")) return true;
    if (!isWarrantyRow(row) && row.getAttribute("data-parent-key")) return true;
    return false;
  }

  function isGroupParentRow(row) {
    return !!row && !isWarrantyRow(row) && !isAccessoryRow(row);
  }

  function insertDividerAfter(anchor) {
    if (!anchor) return;
    if (anchor.tagName === "TR") {
      const colCount = Math.max(1, anchor.children ? anchor.children.length : 4);
      const tr = document.createElement("tr");
      tr.className = "warranty-divider-row";
      tr.setAttribute("aria-hidden", "true");
      const td = document.createElement("td");
      td.className = "warranty-divider-cell";
      td.colSpan = colCount;
      tr.appendChild(td);
      anchor.insertAdjacentElement("afterend", tr);
    } else {
      const div = document.createElement("div");
      div.className = "warranty-divider-row";
      div.setAttribute("aria-hidden", "true");
      anchor.insertAdjacentElement("afterend", div);
    }
  }

  function applyDrawerGroupDividers(rowRoot) {
    if (!rowRoot) return;

    rowRoot
      .querySelectorAll("tr.warranty-divider-row, .warranty-group-divider, .warranty-divider-row")
      .forEach((el) => el.remove());

    const rows = Array.from(rowRoot.querySelectorAll("[data-cart-line][data-line-key]"));
    const parents = rows.filter(isGroupParentRow);
    if (parents.length < 2) return;

    for (let i = 0; i < parents.length - 1; i++) {
      let anchor = parents[i];
      let next = anchor.nextElementSibling;

      while (next && next.matches?.("[data-cart-line][data-line-key]") && !isGroupParentRow(next)) {
        anchor = next;
        next = next.nextElementSibling;
      }

      insertDividerAfter(anchor);
    }
  }

  function reorderDrawerWarrantyLinesAndDividers() {
    const { rows, root } = getDrawerRows();
    if (!rows.length || !root) return;

    // warranty rows indexed by parent variant id
    const warrantyByParentVid = new Map();
    for (const row of rows) {
      if (!isWarrantyRow(row)) continue;
      const parentVid = row.getAttribute("data-warranty-parent-variant-id");
      if (parentVid) warrantyByParentVid.set(parentVid, row);
    }

    // only true group parents in current order
    const parents = rows.filter(isGroupParentRow);

    // place warranty directly under each parent
    for (const p of parents) {
      const parentVid = p.getAttribute("data-variant-id");
      if (!parentVid) continue;

      const w = warrantyByParentVid.get(parentVid);
      if (!w) continue;

      if (w.previousElementSibling !== p) {
        p.insertAdjacentElement("afterend", w);
      }
    }

    applyDrawerGroupDividers(root);
  }

  function scheduleDrawerStablePass() {
    const now = Date.now();
    if (now - STATE.lastScheduleAt < 40) return; // tiny throttle
    STATE.lastScheduleAt = now;

    if (STATE.stableTimer) clearTimeout(STATE.stableTimer);
    STATE.stableHits = 0;
    STATE.stableSig = "";

    const pass = () => {
      const { rows } = getDrawerRows();
      if (!rows.length) return;

      const sig = rowsSignature(rows);
      if (sig === STATE.stableSig) STATE.stableHits += 1;
      else {
        STATE.stableSig = sig;
        STATE.stableHits = 0;
      }

      // stable enough -> apply
      if (STATE.stableHits >= 1) {
        reorderDrawerWarrantyLinesAndDividers();
        return;
      }

      // try again (drawer renders in waves)
      STATE.stableTimer = setTimeout(pass, 120);
    };

    // immediate attempt to reduce visible reorder flicker before the delayed stabilization passes
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        try { reorderDrawerWarrantyLinesAndDividers(); } catch (_) {}
      });
    });

    // multi-pass windows (important!)
    setTimeout(pass, 60);
    setTimeout(pass, 180);
    setTimeout(pass, 420);
  }

  function mutationTouchesDrawer(mutation) {
    const selector = "cart-drawer-component, cart-items-component[data-drawer], [data-cart-line], .cart-drawer__summary";

    if (mutation.target instanceof Element) {
      if (mutation.target.matches?.(selector) || mutation.target.closest?.("cart-drawer-component, cart-items-component[data-drawer]")) {
        return true;
      }
    }

    const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
    return nodes.some((node) => {
      if (!(node instanceof Element)) return false;
      return node.matches?.(selector) || node.querySelector?.(selector);
    });
  }

  function startGlobalDrawerObserver() {
    if (STATE.observerStarted) return;
    STATE.observerStarted = true;

    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type !== "childList") continue;
        if (!mutationTouchesDrawer(m)) continue;

        scheduleDrawerStablePass();
        return;
      }
    });

    mo.observe(document.documentElement, { childList: true, subtree: true });

    // initial
    scheduleDrawerStablePass();
    normalizeCartBubbleCount(false);
    refreshHeaderState();
  }

  window.__warrantySetCartBubbleCount = setCartBubbleCount;
  window.__warrantyNormalizeCartBubbleCount = normalizeCartBubbleCount;

  // ------------------------------------------------------------
  // Init
  // ------------------------------------------------------------
  function init() {

    // Capture=true so we intercept before theme scripts
    document.addEventListener("click", handleATCClickCapture, true);
    document.addEventListener("submit", handleATCSubmitCapture, true);

    document.addEventListener('click', (event) => {
      onProductWarrantyStatusTriggerClick(event);
    });

    document.addEventListener('keydown', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-warranty-box][data-scope="product"] [data-warranty-status-trigger]')) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onProductWarrantyStatusTriggerClick(event);
    });

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-warranty-box][data-scope="product"] [data-warranty-toggle]')) {
        handleProductWarrantyToggleChange(target);
        return;
      }

      if (target.matches('form[action="/cart/add"] [name="id"]')) {
        window.setTimeout(syncProductWarrantyBoxFromCart, 80);
      }
    });

    window.addEventListener('pageshow', () => {
      syncProductWarrantyBoxFromCart();
    });

    document.addEventListener('cart:update', () => {
      syncProductWarrantyBoxFromCart();
    });

    // robust drawer observer (covers re-renders & shadow dom)
    startGlobalDrawerObserver();

    // Also normalize once on load (covers returning visitors with warranty in cart)
    normalizeCartBubbleCount(true);
    refreshHeaderState();
    syncProductWarrantyBoxFromCart();

    document.addEventListener("shopify:section:load", () => {
      scheduleDrawerStablePass();
      refreshHeaderState();
      normalizeCartBubbleCount(false);
      syncProductWarrantyBoxFromCart();
    });
  }

  document.addEventListener("DOMContentLoaded", init);
})();
